import axios from 'axios';
import { notifyError, notifyInfo } from './toast';

// Configured API Base URL and PeerMate Target URL
const API_BASE_URL = (process.env.REACT_APP_BASE_URL || 'https://app.peer2learn.com').replace(/\/+$/, '');
const PEERMATE_BASE_URL = (process.env.REACT_APP_PEERMATE_URL || 'https://peermate.peer2learn.com').replace(/\/+$/, '');

// Guard to prevent duplicate concurrent invocations (double clicks)
let isHandoffInProgress = false;

/**
 * Check if a handoff operation is currently in flight.
 * @returns {boolean}
 */
export const isHandoffBusy = () => isHandoffInProgress;

/**
 * Constructs the target PeerMate URL containing strictly the single-use ticket.
 * SECURITY: NEVER appends JWT, refresh token, passwords, or student IDs.
 *
 * @param {string} ticket - Cryptographic single-use ticket
 * @returns {string} Fully qualified PeerMate handoff URL
 */
export const buildPeerMateHandoffUrl = (ticket) => {
  if (!ticket || typeof ticket !== 'string' || ticket.trim().length === 0) {
    throw new Error('Valid ticket is required to construct PeerMate URL');
  }
  return `${PEERMATE_BASE_URL}?ticket=${encodeURIComponent(ticket.trim())}`;
};

/**
 * Initiates a secure handoff from Peer2Learn to PeerMate AI.
 * 
 * 1. Checks in-flight lock to prevent duplicate rapid requests.
 * 2. Retrieves authenticated student JWT from localStorage ('token').
 * 3. Requests single-use handoff ticket via POST /api/auth/students/handoff-ticket.
 * 4. Validates ticket.
 * 5. Safely navigates the browser to https://peermate.peer2learn.com?ticket=<TICKET>.
 * 
 * @param {Object} options
 * @param {Function} [options.navigateFn] - Optional custom navigation handler for testing (defaults to window.location.assign)
 * @param {boolean} [options.silentToast] - Suppress info toast
 * @returns {Promise<{ success: boolean, url?: string, error?: string }>}
 */
export const navigateToPeerMate = async (options = {}) => {
  const { navigateFn = null, silentToast = false } = options;

  // 1. Guard against rapid duplicate clicks
  if (isHandoffInProgress) {
    return { success: false, error: 'Handoff already in progress' };
  }

  // 2. Retrieve existing authenticated session token
  let token = null;
  try {
    token = localStorage.getItem('token');
  } catch (e) {
    console.error('Storage access error:', e);
  }

  if (!token) {
    notifyError('Please log in to your account to launch PeerMate AI.');
    return { success: false, error: 'Unauthenticated' };
  }

  isHandoffInProgress = true;
  if (!silentToast) {
    notifyInfo('Connecting to PeerMate AI...');
  }

  try {
    // 3. Request short-lived single-use handoff ticket from backend
    const response = await axios.post(
      `${API_BASE_URL}/api/auth/students/handoff-ticket`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        timeout: 10000 // 10-second timeout guard
      }
    );

    // 4. Validate ticket presence
    const ticket = response?.data?.ticket;
    if (!ticket || typeof ticket !== 'string' || ticket.trim().length === 0) {
      throw new Error('Malformed response: missing ticket');
    }

    // 5. Construct safe URL (ONLY ticket appended, no JWT or credentials)
    const targetUrl = buildPeerMateHandoffUrl(ticket);

    // 6. Redirect browser to PeerMate cross-origin app
    if (typeof navigateFn === 'function') {
      navigateFn(targetUrl);
    } else if (typeof window !== 'undefined' && window.location) {
      window.location.assign(targetUrl);
    }

    return { success: true, url: targetUrl };
  } catch (err) {
    const status = err.response?.status;

    if (status === 401) {
      notifyError('Your session has expired. Please log in again.');
    } else if (status === 403) {
      notifyError('Access restricted. Please verify your student account.');
    } else {
      notifyError('Unable to launch PeerMate AI. Please try again.');
    }

    // Log diagnostic error without exposing auth tokens
    console.error('PeerMate handoff error:', err.message || 'Request failed');

    return {
      success: false,
      error: err.message || 'Handoff failed'
    };
  } finally {
    // Release in-flight lock after network trip completes
    isHandoffInProgress = false;
  }
};
